import type { Project } from "../lib/types.js";

export type Audience = "kids" | "teens" | "adults" | "all";
export type Difficulty = "very-simple" | "simple" | "moderate" | "detailed" | "intricate";
export type Consistency = "flexible" | "balanced" | "strict";
export type PageStatus = "planned" | "generating" | "generated" | "needs-review" | "approved";

export const AUDIENCE_OPTIONS: { id: Audience; label: string }[] = [
  { id: "kids", label: "Kids" },
  { id: "teens", label: "Teens" },
  { id: "adults", label: "Adults" },
  { id: "all", label: "All ages" }
];

export const DIFFICULTY_OPTIONS: { id: Difficulty; label: string }[] = [
  { id: "very-simple", label: "Very simple" },
  { id: "simple", label: "Simple" },
  { id: "moderate", label: "Moderate" },
  { id: "detailed", label: "Detailed" },
  { id: "intricate", label: "Intricate" }
];

export const TRIM_OPTIONS: { id: string; label: string; width: number; height: number }[] = [
  { id: "8.5x11", label: "8.5 × 11 in", width: 8.5, height: 11 },
  { id: "8x10", label: "8 × 10 in", width: 8, height: 10 },
  { id: "custom", label: "Custom", width: 8.5, height: 11 }
];

export const STATUS_LABEL: Record<PageStatus, string> = {
  planned: "Planned",
  generating: "Generating",
  generated: "Generated",
  "needs-review": "Needs review",
  approved: "Approved"
};

export interface StyleBible {
  visualStyle: string;
  lineWeight: string;
  complexity: string;
  characterProportions: string;
  backgroundComplexity: string;
  openSpace: string;
  shadingRules: string;
  perspectiveRules: string;
  composition: string;
  avoid: string;
  pageBorder: boolean;
}

export interface CharacterData {
  description: string;
  species: string;
  age: string;
  body: string;
  face: string;
  hair: string;
  clothing: string;
  accessories: string;
  personality: string;
  distinguishing: string;
  mustHave: string;
  mustNotChange: string;
}

export interface PrintSettings {
  bleed: boolean;
  sided: "single" | "double";
  blankBacks: boolean;
  marginIn: number;
  pageNumbers: boolean;
  includeCover: boolean;
  titlePage: boolean;
  copyrightPage: boolean;
  belongsToPage: boolean;
  testPage: boolean;
  copyrightText: string;
  paperType: "white" | "cream" | "color";
  paperThicknessIn: number;
  bleedIn: number;
  gutterIn: number;
}

export interface CoverSettings {
  author: string;
  style: string;
  description: string;
  backDescription: string;
  backText: string;
  frontVariantId: string | null;
  backVariantId: string | null;
  titleColor: string;
  subtitleColor: string;
  textColor: string;
  backgroundColor: string;
  titlePlate: boolean;
}

export interface ColoringBook {
  projectId: string;
  audience: Audience;
  difficulty: Difficulty;
  pageCount: number;
  trim: string;
  trimWidth: number;
  trimHeight: number;
  concept: string | null;
  brief: string | null;
  consistency: Consistency;
  imageProvider: string | null;
  imageModel: string | null;
  seed: number | null;
  style: StyleBible;
  print: PrintSettings;
  cover: CoverSettings;
}

export interface ColoringCharacter {
  id: string;
  projectId: string;
  name: string;
  data: CharacterData;
  sheetVariantId: string | null;
  position: number;
}

export interface QualityCheck {
  id: string;
  label: string;
  status: "pass" | "warn" | "info";
  detail?: string;
}

export interface QualityReport {
  checkedAt: string;
  variantId: string;
  checks: QualityCheck[];
}

export interface ColoringPage {
  id: string;
  projectId: string;
  position: number;
  title: string;
  scene: string;
  characterIds: string[];
  location: string;
  props: string;
  composition: string;
  prompt: string | null;
  negativePrompt: string | null;
  status: PageStatus;
  currentVariantId: string | null;
  approvedVariantId: string | null;
  quality: QualityReport | null;
}

export type JobStatus = "queued" | "generating" | "done" | "error" | "canceled";

export interface ColoringJob {
  id: string;
  projectId: string;
  pageId: string;
  kind: string;
  instruction: string | null;
  status: JobStatus;
  attempts: number;
  error: string | null;
}

export interface ColoringState {
  project: Project;
  book: ColoringBook;
  characters: ColoringCharacter[];
  pages: ColoringPage[];
  jobs: ColoringJob[];
}

export type PageOp = "generate" | "variant" | "revise" | "simplify" | "add-detail" | "remove-detail" | "improve-lineart";

export interface ImageCapabilities {
  textToImage: boolean;
  referenceImages: boolean;
  maxReferenceImages: number;
  imageEdit: boolean;
  imageToImage: boolean;
  negativePrompt: boolean;
  seed: boolean;
  aspectRatios: string[];
  maxResolution: string;
}

export interface ImageProviderInfo {
  id: string;
  label: string;
  configured: boolean;
  model: string | null;
  capabilities: ImageCapabilities;
  hint: string;
}

export interface ProviderStatus {
  providers: ImageProviderInfo[];
  active: { id: string; label: string; model: string; capabilities: ImageCapabilities } | null;
  chosen: string | null;
  costConfigured: boolean;
}

export interface CostEstimate {
  count: number;
  currency: "USD" | null;
  min: number | null;
  max: number | null;
  message: string | null;
}

export interface ColoringVariant {
  id: string;
  projectId: string;
  ownerType: "page" | "character" | "cover";
  ownerId: string;
  contentType: string;
  source: "generated" | "uploaded" | "edited";
  provider: string | null;
  model: string | null;
  prompt: string | null;
  negativePrompt: string | null;
  seed: number | null;
  width: number | null;
  height: number | null;
  settings: { op?: string; mode?: string; instruction?: string | null; consistency?: string; notes?: string[]; [k: string]: unknown };
  refs: string[];
  createdAt: string;
}

export interface FixResult {
  variantId: string;
  fixed: string[];
  notFixable: string[];
  report: QualityReport;
  page: ColoringPage;
}

export type Leaf =
  | { kind: "title" }
  | { kind: "copyright" }
  | { kind: "belongs" }
  | { kind: "test" }
  | { kind: "art"; pageId: string; number: number }
  | { kind: "blank"; reason: "front" | "back" | "pad" };

export interface PreviewPlan {
  leaves: Leaf[];
  pages: { id: string; title: string; variantId: string; number: number }[];
  frontVariantId: string | null;
  backVariantId: string | null;
}

export interface WrapDimensions {
  interiorLeaves: number;
  spineIn: number;
  widthIn: number;
  heightIn: number;
  bleedIn: number;
  spineTextAllowed: boolean;
}

export interface ExportSummary {
  pages: { total: number; withArt: number; missing: number[]; unapproved: number };
  interior: { leaves: number; blanks: number; titlePages: number; belowMinimum: boolean };
  kdp: WrapDimensions & { trimWidthIn: number; trimHeightIn: number; bleed: boolean; marginIn: number; gutterIn: number; paperThicknessIn: number; paperType: string };
  hasFrontCover: boolean;
  hasBackCover: boolean;
}

export type ExportKind = "pdf" | "digital-pdf" | "kdp-interior" | "kdp-cover" | "zip";

export type PageInput = Partial<
  Pick<ColoringPage, "title" | "scene" | "characterIds" | "location" | "props" | "composition" | "prompt" | "negativePrompt" | "status">
>;

export type BookPatch = Partial<
  Omit<ColoringBook, "projectId" | "style" | "print" | "cover"> & {
    style: Partial<StyleBible>;
    print: Partial<PrintSettings>;
    cover: Partial<CoverSettings>;
  }
>;
